/**
 * ActivityTimeline — Unified vertical timeline merging all event sources.
 */

import {
  MessageSquare,
  Mail,
  Phone,
  RefreshCw,
  CheckCircle,
  XCircle,
  ArrowRight,
  Workflow,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Bell,
} from 'lucide-react';
import { useProspectTimeline } from '@/lib/api/hooks/useProspectTimeline';
import type { TimelineItem } from '@/types/events';

interface ActivityTimelineProps {
  prospectId: string;
}

function getIcon(item: TimelineItem) {
  const type = item.type;
  if (type.startsWith('sms_outbound')) return <MessageSquare className="h-3.5 w-3.5 text-blue-500" />;
  if (type.startsWith('sms_inbound') || type === 'sms_reply') return <MessageSquare className="h-3.5 w-3.5 text-green-500" />;
  if (type === 'email_sent' || type === 'enrichment_email_sent') return <Mail className="h-3.5 w-3.5 text-blue-500" />;
  if (type === 'email_opened') return <Eye className="h-3.5 w-3.5 text-green-500" />;
  if (type === 'email_clicked') return <MousePointerClick className="h-3.5 w-3.5 text-green-600" />;
  if (type === 'email_delivered') return <CheckCircle className="h-3.5 w-3.5 text-blue-400" />;
  if (type === 'email_bounced' || type === 'email_dropped') return <XCircle className="h-3.5 w-3.5 text-red-500" />;
  if (type === 'email_replied') return <Mail className="h-3.5 w-3.5 text-green-500" />;
  if (type === 'email_complained') return <AlertTriangle className="h-3.5 w-3.5 text-red-500" />;
  if (type.includes('call')) return <Phone className="h-3.5 w-3.5 text-purple-500" />;
  if (type.includes('sequence')) return <Workflow className="h-3.5 w-3.5 text-orange-500" />;
  if (type === 'status_changed') return <ArrowRight className="h-3.5 w-3.5 text-gray-500" />;
  if (type === 'unsubscribed') return <XCircle className="h-3.5 w-3.5 text-red-400" />;
  if (type === 'research_completed') return <RefreshCw className="h-3.5 w-3.5 text-indigo-500" />;
  return <Bell className="h-3.5 w-3.5 text-muted-foreground" />;
}

function getDotColor(item: TimelineItem): string {
  const type = item.type;
  if (type.includes('failed') || type.includes('bounced') || type.includes('complained') || type.includes('dropped')) return 'bg-red-500';
  if (type.includes('inbound') || type.includes('reply') || type.includes('opened') || type.includes('clicked') || type.includes('delivered')) return 'bg-green-500';
  if (type.includes('outbound') || type.includes('sent') || type.startsWith('email_')) return 'bg-blue-500';
  if (type.includes('sequence')) return 'bg-orange-500';
  if (type.includes('call')) return 'bg-purple-500';
  return 'bg-gray-400';
}

function formatRelative(timestamp: string): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function TimelineEntry({ item }: { item: TimelineItem }) {
  return (
    <div className="flex gap-3 group">
      {/* Dot + line */}
      <div className="flex flex-col items-center">
        <div className={`w-2 h-2 rounded-full mt-1.5 ${getDotColor(item)}`} />
        <div className="w-px flex-1 bg-border group-last:hidden" />
      </div>

      {/* Content */}
      <div className="pb-3 flex-1 min-w-0">
        <div className="flex items-start gap-2">
          {getIcon(item)}
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-tight">{item.description}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{formatRelative(item.timestamp)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ActivityTimeline({ prospectId }: ActivityTimelineProps) {
  const { data, isLoading } = useProspectTimeline(prospectId);
  const items = data?.timeline || [];

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-2">Loading timeline...</div>;
  }

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>;
  }

  return (
    <div className="max-h-72 overflow-y-auto pr-1">
      {items.map((item) => (
        <TimelineEntry key={item.id} item={item} />
      ))}
    </div>
  );
}
